import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { FormContext } from '../Form/FormContext';
import { FormFieldContext } from './FormFieldContext';
import { InlineGroup, InlineGroupItem } from '../InlineGroup/InlineGroup';
/**
 * Values for {@link FormField.propTypes.type} prop.
 */
const typeValues = ['input', 'autosuggest', 'textarea', 'select', 'checkbox', 'combobox', 'other', 'group'];
/**
 * If {@link required} is set, render a `required` flag according {@link requiredText}.
 */
function renderIfRequired(required, requiredText) {
    return required ? React.createElement("span", { "aria-hidden": "true" }, requiredText) : null;
}
/**
 * Bronson FormField component.
 * @see https://bronson.vwfs.io/default/components/detail/bronson-form-field.html
 * @constructor
 */
export function FormField({ children, className, errorMessage, extraContent, hint, infoIcon, inline, labelElement = 'label', labelForId, labelId, labelText, noFloatingLabel, required, requiredText, staticLabel, type, testId, ...otherProps }) {
    const context = React.useContext(FormContext);
    const [elementActive, setElementActive] = useState(false);
    const contextValue = {
        errorMessage,
        setElementActive: (active) => setElementActive(active),
    };
    const formFloatingLabel = context && context.floatingLabel;
    useEffect(() => {
        if (!type) {
            const valueHint = typeValues.join(', ');
            if (formFloatingLabel) {
                // eslint-disable-next-line no-console
                console.error(`Warning: The prop \`type\` is required in \`<FormField>\` (possible values: ${valueHint}) because floating labels are enabled in \`<Form>\` element.`);
            }
            else {
                // eslint-disable-next-line no-console
                console.warn(`The prop \`type\` should be added to \`<FormField>\` (possible values: ${valueHint}). This warning has no negative effect because floating labels are not enabled in the \`<Form>\` element.`);
            }
        }
    });
    const standardLabel = 
    // <Form floatingLabel> prop not set
    !formFloatingLabel ||
        // no floating when explicitly set
        noFloatingLabel ||
        // downstream element is active (e.g. contains text, triggered via onChange in element on callback)
        elementActive ||
        // no floating if not in [input, textarea]
        (type !== 'input' && type !== 'textarea') ||
        // No floating with {@link InfoIcon}.
        infoIcon;
    const divClassNameList = classNames('c-form-field', {
        'c-form-field--no-floating': noFloatingLabel,
        'c-form-field--static': staticLabel,
        'c-form-field--textarea': type === 'textarea',
        'is-active': formFloatingLabel && standardLabel && !staticLabel,
        'c-form-field--inline': inline,
    }, className).trim();
    const CustomTagLabelElement = `${labelElement}`;
    function renderIfLabel() {
        if (labelText) {
            return (React.createElement(CustomTagLabelElement, { id: labelId, htmlFor: labelElement === 'label' && labelForId ? labelForId : undefined, className: "c-form-field__label" },
                labelText,
                renderIfRequired(required, requiredText),
                renderIfInfoIcon()));
        }
        return null;
    }
    function renderIfInfoIcon() {
        return infoIcon ? React.createElement(React.Fragment, null, infoIcon) : null;
    }
    function renderIfErrorMessage() {
        return errorMessage ? React.createElement(React.Fragment, null, errorMessage) : null;
    }
    function renderIfExtraContent() {
        if (extraContent) {
            return React.createElement("div", { className: "c-form-field__extra" }, extraContent);
        }
        return null;
    }
    function renderIfHint() {
        if (hint) {
            /**
             * @type {string | React.ReactNode}
             * Type string can be used for text only.
             * Type React.ReactNode can be used for links (`<a href='#'>`) or to include button with {@link onClick} functionality.
             */
            return (React.createElement("p", { className: "c-form-field__hint", id: `hint-${labelForId}`, "data-testid": testId && `hint-${testId}` }, hint));
        }
        return null;
    }
    return (React.createElement("div", { ...otherProps, "data-testid": testId, className: divClassNameList },
        renderIfLabel(),
        React.createElement("div", { className: "c-form-field__box" },
            React.createElement(FormFieldContext.Provider, { value: contextValue }, children),
            renderIfErrorMessage(),
            renderIfExtraContent()),
        renderIfHint()));
}
export function FormFieldRadioButtonGroup({ 
/** Custom props */
disabled, legendVisuallyHidden, 
/** _InternalFormFieldGroupProps */
grid, small, 
/** FormField Props */
children, extraContent, errorMessage, hint, infoIcon, labelId, labelText, onClick, required, requiredText, testId, ...otherProps }) {
    const legendClassName = classNames('c-form-field__label', { 'u-visually-hidden': legendVisuallyHidden });
    function renderIfInfoIcon() {
        return infoIcon ? React.createElement(React.Fragment, null, infoIcon) : null;
    }
    function renderIfErrorMessage() {
        return errorMessage ? React.createElement(React.Fragment, null, errorMessage) : null;
    }
    function renderIfExtraContent() {
        if (extraContent) {
            return React.createElement("div", { className: "c-form-field__extra" }, extraContent);
        }
        return null;
    }
    function renderIfHint() {
        if (hint) {
            /**
             * If the {@link onClick} is set we use an {@link HTMLAnchorElement} as hint,
             * otherwise we fall back to {@link HTMLParagraphElement}.
             * @type {string}
             */
            const HintElement = onClick ? 'a' : 'p';
            return (React.createElement(HintElement, { className: "c-form-field__hint", id: `hint-${labelId ?? ''}`, "data-testid": testId && `hint-${testId}`, onClick: onClick }, hint));
        }
        return null;
    }
    return (React.createElement("fieldset", { ...otherProps, "data-testid": testId, className: "c-form-field c-form-field--static", disabled: disabled ? disabled : undefined, "aria-required": required ? 'true' : undefined, "aria-invalid": errorMessage ? 'true' : undefined },
        React.createElement("legend", { id: labelId, className: legendClassName },
            labelText,
            renderIfRequired(required, requiredText),
            renderIfInfoIcon()),
        React.createElement("div", { className: "c-form-field__box" },
            React.createElement(_InternalFormFieldGroup, { grid: grid, small: small }, children),
            renderIfErrorMessage(),
            renderIfExtraContent()),
        renderIfHint()));
}
export function FormFieldCheckboxGroup({ 
/** Custom props */
disabled, legendVisuallyHidden, 
/** _InternalFormFieldGroupProps */
grid, small, 
/** FormField Props */
children, extraContent, errorMessage, hint, infoIcon, labelId, labelText, onClick, required, requiredText, testId, ...otherProps }) {
    const legendClassName = classNames('c-form-field__label', { 'u-visually-hidden': legendVisuallyHidden });
    function renderIfInfoIcon() {
        return infoIcon ? React.createElement(React.Fragment, null, infoIcon) : null;
    }
    function renderIfErrorMessage() {
        return errorMessage ? React.createElement(React.Fragment, null, errorMessage) : null;
    }
    function renderIfExtraContent() {
        if (extraContent) {
            return React.createElement("div", { className: "c-form-field__extra" }, extraContent);
        }
        return null;
    }
    function renderIfHint() {
        if (hint) {
            /**
             * If the {@link onClick} is set we use an {@link HTMLAnchorElement} as hint,
             * otherwise we fall back to {@link HTMLParagraphElement}.
             * @type {string}
             */
            const HintElement = onClick ? 'a' : 'p';
            return (React.createElement(HintElement, { className: "c-form-field__hint", id: `hint-${labelId ?? ''}`, "data-testid": testId && `hint-${testId}`, onClick: onClick }, hint));
        }
        return null;
    }
    return (React.createElement("fieldset", { ...otherProps, "data-testid": testId, className: "c-form-field c-form-field--static", disabled: disabled ? disabled : undefined },
        React.createElement("legend", { id: labelId, className: legendClassName },
            labelText,
            renderIfRequired(required, requiredText),
            renderIfInfoIcon()),
        React.createElement("div", { className: "c-form-field__box" },
            React.createElement(_InternalFormFieldGroup, { grid: grid, small: small }, children),
            renderIfErrorMessage(),
            renderIfExtraContent()),
        renderIfHint()));
}
export function FormFieldExtra({ children, ...otherProps }) {
    return (children && (React.createElement("div", { className: "c-form-field__extra", ...otherProps }, children)));
}
/*
 * Internal Bronson RadioButtonGroup component.
 */
function _InternalFormFieldGroup({ children, grid, small, ...otherProps }) {
    function renderChildren() {
        return React.Children.map(children, (child) => {
            if (!child) {
                return null;
            }
            return React.createElement(InlineGroupItem, { ...otherProps }, child);
        });
    }
    return (React.createElement(InlineGroup, { grid: grid, small: small }, renderChildren()));
}
